import React, { useEffect, useState } from 'react';
import RefreshPanel from '../../components/RefreshPanel';
import ScheduledRefreshPanel from '../../components/ScheduledRefreshPanel';
import PhiConfigModal from '../../components/PhiConfigModal';
import { useCache } from '../../hooks/useCache';
import './AdminPages.css';

function DataSources() {
  const cache = useCache();
  const [showPhiConfig, setShowPhiConfig] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    cache.fetchCacheStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async (dataTypes, start, end, mode) => {
    const result = await cache.refreshData(dataTypes, start, end, mode);

    if (result && result.success) {
      const totalRecords = result.results.reduce((sum, r) => sum + (r.recordCount || 0), 0);
      const successTypes = result.results.filter(r => r.success);

      // Find actual date range from records that were loaded
      const allDates = result.results
        .filter(r => r.actualStartDate && r.actualEndDate)
        .flatMap(r => [new Date(r.actualStartDate), new Date(r.actualEndDate)]);

      let dateRangeMsg = `from ${start} to ${end}`;
      if (allDates.length > 0) {
        const actualStart = new Date(Math.min(...allDates));
        const actualEnd = new Date(Math.max(...allDates));
        const formatDate = (d) => d.toISOString().split('T')[0];
        dateRangeMsg = `(actual data: ${formatDate(actualStart)} to ${formatDate(actualEnd)})`;
      }

      setSuccessMessage(
        `✓ Successfully refreshed ${successTypes.length} data type${successTypes.length !== 1 ? 's' : ''} ` +
        `(${totalRecords.toLocaleString()} records) ${dateRangeMsg}`
      );
      setTimeout(() => setSuccessMessage(null), 12000);
    } else if (result && !result.success) {
      const failedTypes = result.results.filter(r => !r.success);
      const hints = failedTypes
        .map(r => `${r.dataType}${r.error ? `: ${String(r.error).slice(0, 80)}${String(r.error).length > 80 ? '…' : ''}` : ''}`)
        .slice(0, 4)
        .join(' · ');
      setSuccessMessage(
        `⚠ Partial refresh: ${failedTypes.length} type${failedTypes.length !== 1 ? 's' : ''} failed. ${hints}${failedTypes.length > 4 ? ' …' : ''}`
      );
      setTimeout(() => setSuccessMessage(null), 15000);
    }
  };

  const handleRefreshFacilities = async () => {
    const result = await cache.refreshFacilities();
    if (result && result.success) {
      setSuccessMessage(`✓ Successfully synced ${result.recordCount || 0} facilities from Salesforce`);
      setTimeout(() => setSuccessMessage(null), 8000);
    }
  };

  const handleCancelRefresh = async () => {
    await cache.cancelRefresh();
  };

  // Wraps cache.importCacheSnapshot so the page-level success banner fires
  // the same way it does after a normal refresh. Keeps the per-table detail
  // (and rollback hint) inside CacheImportSection where it's expanded.
  const handleImportSnapshot = async (file, opts) => {
    const result = await cache.importCacheSnapshot(file, opts);
    if (result?.success) {
      const totalInserted = Object.values(result.tables || {})
        .reduce((sum, s) => sum + (s.rowsInserted || 0), 0);
      setSuccessMessage(
        `✓ Imported snapshot — ${totalInserted.toLocaleString()} rows across ` +
        `${Object.keys(result.tables || {}).length} table${Object.keys(result.tables || {}).length !== 1 ? 's' : ''} ` +
        `in ${(result.elapsedMs / 1000).toFixed(1)}s` +
        (result.backupSuffix ? ` (backup suffix: ${result.backupSuffix})` : '')
      );
      setTimeout(() => setSuccessMessage(null), 12000);
    }
    return result;
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h2>Data Sources</h2>
          <p>
            Manage how VitalData pulls and caches data. Use Smart Refresh for
            daily catch-up (inserts records that aren't already cached). Full
            Refresh of Range replaces only the selected types inside the date
            window. Full Refresh Wipe deletes all cached history for those types
            and reloads just the window — confirm before using it. Salesforce
            facility sync is optional and can be paused with the schedule
            checkbox or <code>SALESFORCE_SYNC_ENABLED=false</code>; the last
            cached hierarchy remains available either way.
          </p>
        </div>
      </div>

      {successMessage && (
        <div className="success-banner">
          {successMessage}
        </div>
      )}

      <RefreshPanel
        cacheStatus={cache.cacheStatus}
        onRefresh={handleRefresh}
        onRefreshFacilities={handleRefreshFacilities}
        onCancelRefresh={handleCancelRefresh}
        onOpenPhiConfig={() => setShowPhiConfig(true)}
        onImportSnapshot={handleImportSnapshot}
        dataRefreshing={cache.dataRefreshing}
        facilitiesRefreshing={cache.facilitiesRefreshing}
        refreshProgress={cache.refreshProgress}
        defaultExpanded
      />

      <ScheduledRefreshPanel />

      {showPhiConfig && (
        <PhiConfigModal
          onClose={() => setShowPhiConfig(false)}
          getPhiConfig={cache.getPhiConfig}
          updatePhiConfig={cache.updatePhiConfig}
          deletePhiConfig={cache.deletePhiConfig}
        />
      )}
    </div>
  );
}

export default DataSources;
